import clsx from 'clsx';
import { type ComponentPropsWithRef } from 'react';

import { Icon } from '@gitbook/spine-icons';
import { assert } from '@gitbook/util-ts';

import {
    HeadlessTree,
    HeadlessTreeItem,
    type HeadlessTreeItemProps,
    type HeadlessTreeProps,
    treeDropIndicatorClassName,
} from '../Tree';
import type { IconDefinition } from '../util';
import { SidebarItemButton, SidebarItemIcon, useSidebarItem } from './SidebarItem';

export interface SidebarTreeItemValue {
    id: string;
    title: string;
    icon: IconDefinition;
}

export interface SidebarTreeProps<T extends SidebarTreeItemValue> extends HeadlessTreeProps<T> {}

export function SidebarTree<T extends SidebarTreeItemValue>(props: SidebarTreeProps<T>) {
    return (
        <HeadlessTree<T> {...props} className={clsx(treeDropIndicatorClassName, props.className)} />
    );
}

export interface SidebarTreeItemProps<T extends SidebarTreeItemValue> extends Omit<
    HeadlessTreeItemProps<T>,
    'textValue' | 'value'
> {
    isActive?: boolean;
    value?: T;
}

export function SidebarTreeItem<T extends SidebarTreeItemValue>(props: SidebarTreeItemProps<T>) {
    const { value, isActive } = props;
    assert(value);
    const { itemProps } = useSidebarItem({
        label: value.title,
        elementType: 'tree-item',
        className: clsx(
            'my-px cursor-pointer pl-[calc((var(--tree-item-level)-1)*0.75rem+var(--px))]',
            // Dragging
            'data-[dragging]:opacity-drag-preview',
            // Drop target
            'data-[drop-target]:outline-1 data-[drop-target]:outline-neutral data-[drop-target]:outline-dashed'
        ),
        isActive,
    });
    return (
        <HeadlessTreeItem {...itemProps} {...props} textValue={value.title}>
            {props.children}
        </HeadlessTreeItem>
    );
}

export interface SidebarTreeItemExpandButtonProps {
    icon: IconDefinition;
}

export function SidebarTreeItemExpandButton(props: SidebarTreeItemExpandButtonProps) {
    return (
        <div className="relative flex shrink-0">
            <SidebarItemButton
                icon={
                    <Icon.ChevronRight className="transition group-aria-expanded/sidebar-item:rotate-90" />
                }
                slot="chevron"
            />
            <div className="pointer-events-none absolute inset-0 flex items-center transition group-hover/sidebar-item:opacity-0 group-data-[focus-visible]/sidebar-item:opacity-0 peer-focus:opacity-0">
                <SidebarItemIcon icon={props.icon} />
            </div>
        </div>
    );
}

export interface SidebarTreeItemTextProps extends ComponentPropsWithRef<'span'> {}

export function SidebarTreeItemText({ ref, ...props }: SidebarTreeItemTextProps) {
    return (
        <span ref={ref} {...props} className={clsx('flex-1 truncate text-left', props.className)} />
    );
}
